import type { TireHotelRehomeListApiData, TireHotelRehomeRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import {
  ApiErrorPageInternal,
  filterUnitFor,
  NoddiButton,
  NoddiTable,
  useFilterUnits,
  usePaginationQueryParam
} from 'noddi-ui-web';
import { TIRE_HOTEL_REHOME_STATUS_OPTIONS } from 'noddi-util';
import { useNavigate } from 'react-router-dom';
import { copyLicensePlateAction } from '../CarDetails/copyLicensePlateAction';
import { rehomeListColumns } from './rehomeColumns';
import type { TireHotelRoutes } from './types';

type TireHotelRehomeFilterInput = Omit<
  NonNullable<TireHotelRehomeListApiData['query']>,
  'pageIndex' | 'pageSize' | 'ordering' | 'search'
>;

const rehomeFilterUnit = filterUnitFor<TireHotelRehomeFilterInput>();

interface Props {
  routes: TireHotelRoutes;
  fromTireHotelId?: number;
  fillHeight?: boolean;
  showNewTransferButton?: boolean;
}

export const TireHotelOwnershipTransfersTable = ({
  routes,
  fromTireHotelId,
  fillHeight = false,
  showNewTransferButton = false
}: Props) => {
  const navigate = useNavigate();
  const { pagination, setPagination } = usePaginationQueryParam();
  const filters = useFilterUnits([
    rehomeFilterUnit.locked({ fromTireHotelIds: fromTireHotelId != null ? [fromTireHotelId] : undefined }),
    rehomeFilterUnit.multiSelect('statuses', {
      title: 'Status',
      options: TIRE_HOTEL_REHOME_STATUS_OPTIONS,
      exclude: true
    })
  ]);

  const {
    data: rehomes,
    isPending,
    isFetching,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedTireHotelRehomes,
    input: {
      ...pagination,
      ...filters.apiInput
    }
  });

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-4'>
      {showNewTransferButton && routes.rehomeCreate && (
        <div className='flex justify-end'>
          {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
          <NoddiButton startIcon='Plus' onClick={() => navigate(routes.rehomeCreate!.getPath({ fromTireHotelId }))}>
            Transfer ownership
          </NoddiButton>
        </div>
      )}
      <NoddiTable
        actions={[copyLicensePlateAction((row: TireHotelRehomeRecord) => row.car.licensePlateNumber)]}
        fillHeight={fillHeight}
        data={rehomes?.results}
        setPagination={setPagination}
        pagination={pagination}
        totalCount={rehomes?.count}
        isPending={isPending}
        getUrlPath={routes.rehomeDetail ? (row) => ({ url: routes.rehomeDetail!.getPath({ id: row.id }) }) : undefined}
        isFetching={isFetching}
        columns={rehomeListColumns}
        filters={filters}
        getRowId={(row) => row.id}
      />
    </div>
  );
};
